import {
  Stack,
  Box,
  Text,
  Collapsible,
  Viewport,
  useViewport,
  useWindowHeight,
  getExpiryDate,
  setCookie,
  storage,
  Inline,
  UseMixpanelProps,
  Mixpanel,
  useDevice,
} from '@treatwell/ui';
import {EmployeeSelectContainer} from '../EmployeeSelect/EmployeeSelectContainer';
import {countDaysFromCurrentDate} from '../../utils/countDaysFromCurrentDate';

import {OrderListContainer} from '../OrderList/OrderList';
import {
  PageContainer,
  GridLayout,
  GridLayoutContent,
  GridLayoutSide,
} from '../../pages';
import {CalendarNavigation} from '../CalendarNavigation';
import {EmptyBasket} from '../EmptyBasket/EmptyBasket';

import {useAppDispatch, useAppSelector} from '../../hooks/store';
import {
  fetchApiAvailability,
  resetAvailability,
} from '../../store/slices/availability/slice';
import {
  selectAvailabilityRequestStatus,
  selectDays,
  selectPaginationFutureRequestStatus,
  selectShowMultiStaffSelection,
  selectTimeslots,
} from '../../store/slices/selectors';
import {memo, useCallback, useEffect} from 'react';
import {getUrlInfo} from '../../utils/getUrlInfo';
import {LoadingSpinner} from '../LoadingSpinner';
import {AddServiceLink} from '../AddServiceLink';
import {selectVenueData, selectVenueId} from '../../store/slices/venue/slice';
import {useTranslation} from 'react-i18next';
import {OrderTotalContainer} from '../OrderTotal/OrderTotal';
import {isAvailabiltyRoute} from '../../utils/isRoute';
import {RouteAvailability} from '../Router';
import {RouteReschedule} from '../Router/Router';
import {RescheduleOrderSubmitContainer} from '../Submit/Submit';
import {selectRescheduleStatus} from '../../store/slices/reschedule/slice';
import {RescheduleSuccessContainer} from '../RescheduleSuccess/RescheduleSuccess';
import {updateLocalStorage} from '../../utils/updateLocalStorage';
import {trackMultiStaffExperimentEnabled} from '../../utils/gtmTracking';
import styles from './App.module.css';
import {SectionTitle} from '../SectionTitle';
import {BackButton} from '../BackButton';
import AppointmentBasket from '../AppointmentBasket/AppointmentBasket';
import {PrefetchCheckoutAssets} from '../PrefetchCheckoutAssets';
import {VenueCardListContainer} from '../AvailableSalonsSuggestions/VenueCardListContainer/VenueCardListContainer';
import {getDeviceId} from '@treatwell/growthbook-js/client';
import {selectEnvironment} from '../../store/slices/config/slice';
import {selectChannelData} from '../../store/slices/channel/slice';
import {useGrowthBook} from '@growthbook/growthbook-react';
import {TrackOnView} from '../Tracking/TrackOnView';
import {SomeResults} from '../AvailableSalonsSuggestions/SalonSuggestionLink';
import {Marketplace} from '../Marketplace';

export const STICKY_FOOTER_VIEWPORT_HEIGHT = 700;

const CalendarGrid = ({
  employeeTitle,
  shouldShowMultiStaffSelection,
  windowHeight,
  showBackButton,
  venueDataUri,
}: {
  employeeTitle: string;
  calendarTitle: string;
  shouldShowMultiStaffSelection?: boolean;
  windowHeight: number;
  showBackButton?: boolean;
  venueDataUri?: string;
}) => (
  <Stack space={shouldShowMultiStaffSelection ? '2md' : 'lg'}>
    <Viewport device="desktop">
      {showBackButton && venueDataUri && <BackButton href={venueDataUri} />}
    </Viewport>
    {shouldShowMultiStaffSelection || (
      <div className={styles.employeeSectionPadding}>
        <Stack as="section" space="xl">
          <Inline space="md" align="center">
            {showBackButton && venueDataUri && (
              <Viewport device={['mobile', 'tablet']}>
                <BackButton href={venueDataUri} hideLabel />
              </Viewport>
            )}
            <SectionTitle title={employeeTitle} type="xlHeader" />
          </Inline>
          <EmployeeSelectContainer />
        </Stack>
      </div>
    )}
    <Stack as="section" space="base">
      <CalendarNavigation />
    </Stack>
    {shouldShowMultiStaffSelection &&
      windowHeight >= STICKY_FOOTER_VIEWPORT_HEIGHT && (
        <Stack>
          <Viewport device="desktop">
            <OrderTotalContainer type="lgHeader" />
          </Viewport>
          <RouteReschedule>
            <Viewport device="desktop">
              <RescheduleOrderSubmitContainer />
            </Viewport>
          </RouteReschedule>
        </Stack>
      )}
  </Stack>
);

const ServiceGrid = ({
  basketTitle,
  venueDataUri,
  addServiceLinkText,
  shouldShowMultiStaffSelection,
}: {
  basketTitle: string;
  venueDataUri: string;
  addServiceLinkText: string;
  shouldShowMultiStaffSelection?: boolean;
}) => {
  const {t} = useTranslation();
  const isMobileOrTablet = useViewport({device: ['mobile', 'tablet']});
  const isOrderListCollapsed =
    shouldShowMultiStaffSelection && isMobileOrTablet;

  return (
    <Stack as="section" space="base">
      {isOrderListCollapsed ? (
        <Box className={styles.collapsibleBox}>
          <Collapsible
            title={
              <Text as="h2">{t('sectionTitle.servicesAndEmployees')}</Text>
            }
            id="servicesAndEmployees"
          >
            <Stack space="xl">
              <OrderListContainer />
              <RouteAvailability>
                <AddServiceLink url={venueDataUri} text={addServiceLinkText} />
              </RouteAvailability>
            </Stack>
          </Collapsible>
        </Box>
      ) : (
        <>
          <SectionTitle title={basketTitle} />
          <OrderListContainer />
        </>
      )}
      <Stack space="2xl">
        {isOrderListCollapsed || (
          <RouteAvailability>
            <AddServiceLink url={venueDataUri} text={addServiceLinkText} />
          </RouteAvailability>
        )}
        {shouldShowMultiStaffSelection || (
          <>
            <Viewport device="desktop">
              <OrderTotalContainer />
            </Viewport>
            <RouteReschedule>
              <Viewport device="desktop">
                <RescheduleOrderSubmitContainer />
              </Viewport>
            </RouteReschedule>
          </>
        )}
      </Stack>
    </Stack>
  );
};

const useFetchAvailabilityOnBack = () => {
  const dispatch = useAppDispatch();
  const venueId = useAppSelector(selectVenueId);

  useEffect(() => {
    const handleNavigation = () => {
      const {url, searchParams} = getUrlInfo(location.href);
      const proposedServices = searchParams.get('proposedServices');
      const servicesObject =
        proposedServices === null ? null : JSON.parse(proposedServices);
      if (proposedServices !== null) {
        dispatch(
          fetchApiAvailability({
            url: url.toString(),
            resetToInitialDate: true,
          }),
        );
      } else {
        dispatch(resetAvailability());
      }
      updateLocalStorage(venueId, servicesObject);
    };
    window.addEventListener('popstate', handleNavigation);
    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, [dispatch, venueId]);
};

export const AppContainer = () => {
  if (isAvailabiltyRoute()) {
    // disabling rules of hooks rule for the following hook as
    // we can be sure this if statement is either true or false depending on the route and
    // there shouldn't be a risk that the order of the hooks will change on subsequent renders
    useFetchAvailabilityOnBack(); // eslint-disable-line react-hooks/rules-of-hooks
  }

  const availabilityStatus = useAppSelector(selectAvailabilityRequestStatus);
  const days = useAppSelector(selectDays);
  const hasDaysData = Object.keys(days).length > 0;
  const isPending = availabilityStatus === 'pending';
  const {t} = useTranslation();
  const venueData = useAppSelector(selectVenueData);
  const paginationRequestStatus = useAppSelector(
    selectPaginationFutureRequestStatus,
  );
  const addServiceLinkText = t('basket.addAnotherService', {
    venueName: venueData.name,
  });
  const employeeTitle = t('calendar.sectionTitle');
  const calendarTitle = t('sectionTitle.calendar');
  const basketTitle = t('sectionTitle.basket');

  const rescheduledOrderStatus = useAppSelector(selectRescheduleStatus);
  const isPaginationRequestPending = paginationRequestStatus === 'pending';
  const showRescheduleSuccess = rescheduledOrderStatus === 'fulfilled';
  const showDateTime = hasDaysData && rescheduledOrderStatus !== 'fulfilled';
  const showSpinnerWithOverlay = isPending && !isPaginationRequestPending;
  const showEmptyBasket = availabilityStatus === 'idle';
  const shouldShowMultiStaffSelection = useAppSelector(
    selectShowMultiStaffSelection,
  );
  const environment = useAppSelector(selectEnvironment);
  const channel = useAppSelector(selectChannelData);
  const gb = useGrowthBook();

  const windowHeight = useWindowHeight();
  useEffect(() => {
    trackMultiStaffExperimentEnabled(shouldShowMultiStaffSelection);
  }, [shouldShowMultiStaffSelection]);

  const mixpanelProps: UseMixpanelProps = {
    isProduction: environment === 'prod',
    channelCode: channel.code,
    getMixpanelFlagValues: useCallback(() => {
      return gb.getFeatureValue('ff-web-frontend__mixpanel-recording', []);
    }, [gb]),
    getExperimentValue: useCallback(
      (experimentName) => {
        return gb.getFeatureValue(experimentName, 'none');
      },
      [gb],
    ),
    getTrackingDeviceId: useCallback(() => {
      return getDeviceId();
    }, []),
  };

  useEffect(() => {
    const urlObject = new URL(location.href);
    const rwgToken = urlObject.searchParams.get('rwg_token');
    const venueId = venueData.id;

    if (!rwgToken) {
      return;
    }

    const rwgCookie = {
      venue_id: venueId,
      rwg_token: decodeURIComponent(rwgToken),
    };

    setCookie({
      name: 'rwg_cookie',
      value: JSON.stringify(rwgCookie),
      expires: getExpiryDate(30),
    });
    storage.session.setItem('rwg', venueId);

    urlObject.searchParams.delete('rwg_token');
    window.history.replaceState('', '', urlObject.toString());
  }, [venueData.id]);

  return (
    <App
      addServiceLinkText={addServiceLinkText}
      showDateTime={showDateTime}
      employeeTitle={employeeTitle}
      calendarTitle={calendarTitle}
      basketTitle={basketTitle}
      venueDataUri={venueData.uri}
      showSpinnerWithOverlay={showSpinnerWithOverlay}
      showEmptyBasket={showEmptyBasket}
      showRescheduleSuccess={showRescheduleSuccess}
      shouldShowMultiStaffSelection={shouldShowMultiStaffSelection}
      windowHeight={windowHeight}
      mixpanelProps={mixpanelProps}
    />
  );
};

type Props = {
  showDateTime: boolean;
  employeeTitle: string;
  calendarTitle: string;
  basketTitle: string;
  venueDataUri: string;
  addServiceLinkText: string;
  showSpinnerWithOverlay: boolean;
  showEmptyBasket: boolean;
  showRescheduleSuccess: boolean;
  shouldShowMultiStaffSelection?: boolean;
  windowHeight: number;
  mixpanelProps: UseMixpanelProps;
};

const App = memo(
  ({
    showDateTime,
    employeeTitle,
    calendarTitle,
    basketTitle,
    venueDataUri,
    addServiceLinkText,
    showSpinnerWithOverlay,
    showEmptyBasket,
    showRescheduleSuccess,
    shouldShowMultiStaffSelection,
    windowHeight,
    mixpanelProps,
  }: Props) => {
    const timeslots = useAppSelector(selectTimeslots);
    const hasTimeslots = timeslots && timeslots.length > 0;
    const channel = useAppSelector(selectChannelData);
    const {isMobile} = useDevice();
    const viewEventAttributes = {
      country: channel.country.countryCode,
      platform: 'web',
      is_web_mobile: isMobile,
      days_from_current_date: countDaysFromCurrentDate(),
    };
    return (
      <PageContainer fixedHeight>
        <RouteAvailability>
          <>
            <PrefetchCheckoutAssets />
            <Mixpanel {...mixpanelProps} />
          </>
        </RouteAvailability>
        <GridLayout isOnMultiStaffSelection={shouldShowMultiStaffSelection}>
          <>
            {showDateTime && (
              <>
                <GridLayoutSide>
                  {shouldShowMultiStaffSelection ? (
                    <ServiceGrid
                      basketTitle={basketTitle}
                      venueDataUri={venueDataUri}
                      addServiceLinkText={addServiceLinkText}
                      shouldShowMultiStaffSelection={
                        shouldShowMultiStaffSelection
                      }
                    />
                  ) : (
                    <Stack space={isMobile ? 'lg' : 'base'}>
                      <CalendarGrid
                        shouldShowMultiStaffSelection={
                          shouldShowMultiStaffSelection
                        }
                        employeeTitle={employeeTitle}
                        windowHeight={windowHeight}
                        calendarTitle={calendarTitle}
                        showBackButton
                        venueDataUri={venueDataUri}
                      />
                      {hasTimeslots && (
                        <Marketplace>
                          <RouteAvailability>
                            <TrackOnView
                              eventName="filter_adjustment_prompt_viewed"
                              eventAttributes={{
                                ...viewEventAttributes,
                                trigger_context:
                                  'availability/filter_adjustment',
                              }}
                            >
                              <SomeResults />
                            </TrackOnView>
                          </RouteAvailability>
                        </Marketplace>
                      )}
                    </Stack>
                  )}
                </GridLayoutSide>
                {shouldShowMultiStaffSelection ? (
                  <GridLayoutContent>
                    <CalendarGrid
                      shouldShowMultiStaffSelection={
                        shouldShowMultiStaffSelection
                      }
                      employeeTitle={employeeTitle}
                      calendarTitle={calendarTitle}
                      windowHeight={windowHeight}
                    />
                  </GridLayoutContent>
                ) : (
                  <Viewport device="desktop">
                    <GridLayoutContent>
                      <Inline
                        justify="end"
                        align="start"
                        className={styles.basketContainer}
                      >
                        <AppointmentBasket />
                      </Inline>
                    </GridLayoutContent>
                  </Viewport>
                )}
                <RouteAvailability>
                  <Marketplace>
                    <VenueCardListContainer />
                  </Marketplace>
                </RouteAvailability>
              </>
            )}
          </>
          <>{showSpinnerWithOverlay && <LoadingSpinner withOverlay />}</>
          <>{showEmptyBasket && <EmptyBasket />}</>
          <>{showRescheduleSuccess && <RescheduleSuccessContainer />}</>
        </GridLayout>
      </PageContainer>
    );
  },
);
